<script lang="ts">
  import { layout, setView } from '../state/layout.svelte'
  import { location } from '../state/location.svelte'
  import ActiveLayerChip from './ActiveLayerChip.svelte'
  import BottomNav from './BottomNav.svelte'
  import CompassButton from './CompassButton.svelte'
  import ForecastSection from './ForecastSection.svelte'
  import LayerMenu from './LayerMenu.svelte'
  import LocateButton from './LocateButton.svelte'
  import MapView from './MapView.svelte'
  import PlacesSection from './PlacesSection.svelte'
  import SaveStar from './SaveStar.svelte'
  import SearchMenu from './SearchMenu.svelte'
  import SectionPanel from './SectionPanel.svelte'
  import SettingsScreen from './SettingsScreen.svelte'
  import NextHourStrip from './forecast/NextHourStrip.svelte'
  import MeteogramPanel from './panels/MeteogramPanel.svelte'
  import ModelStatusPanel from './panels/ModelStatusPanel.svelte'
  import SkillPanel from './panels/SkillPanel.svelte'

  /**
   * The phone (Phase 11 plan §5): a section above a bottom nav. Forecast,
   * Models, Places and Settings each fill the screen under their own panel
   * bar, with the next hour's strip above the nav on the last three (on
   * Forecast it repeated the Next hour section); Map is the map with its
   * own floating chrome. The map stays mounted behind the other sections
   * (§2.1), hidden rather than torn down, so a switch back re-fetches nothing.
   */

  let { cartoKey }: { cartoKey: string } = $props()

  const TITLES = { forecast: 'Forecast', models: 'Models', places: 'Places', settings: 'Settings' }

  let bundle = $derived(location.bundle)
  let onMap = $derived(layout.view === 'map')
  let title = $derived(onMap ? '' : TITLES[layout.view as keyof typeof TITLES])

  let layersOpen = $state(false)
  let map: MapView | undefined = $state()
  let bearing = $state(0)
  let pitch = $state(0)
  $effect(() => {
    if (!onMap) layersOpen = false
  })

  // Searching from Places or Settings means "show me that place"; from
  // Forecast or Models the section on screen already follows the point.
  function searched() {
    if (layout.view === 'places' || layout.view === 'settings') setView('forecast')
  }
</script>

<div class="mobile">
  <div class="stage">
    <div class="map-area">
      <!-- Clear of the search row at the top and the timeline card at the
           bottom, so a flight centres on the map that is showing. -->
      <MapView bind:this={map} bind:bearing bind:pitch {cartoKey} hidden={!onMap} padding={{ top: 120, bottom: 170 }} />

      {#if onMap}
        <div class="search-slot"><SearchMenu variant="field" /></div>
        <div class="chip-slot"><ActiveLayerChip onOpen={() => (layersOpen = true)} /></div>
        <LayerMenu bind:open={layersOpen} />
        <div class="locate-slot"><LocateButton /></div>
        <div class="compass-slot">
          <CompassButton {bearing} {pitch} onReset={() => map?.resetNorth()} />
        </div>
      {/if}
    </div>

    {#if !onMap}
      <div class="section">
        <SectionPanel {title}>
          {#snippet actions()}
            {#if layout.view !== 'settings'}
              <SearchMenu variant="ink" onResolved={searched} />
            {/if}
            {#if location.selected && layout.view !== 'settings'}
              <SaveStar size={18} />
            {/if}
          {/snippet}

          {#if layout.view === 'forecast'}
            <ForecastSection />
          {:else if layout.view === 'models'}
            <!-- The point's whole time axis from every model, how they have
                 been doing here, and whether they are arriving (plan §5.4).
                 Each still opens on a tap and fetches nothing until then. -->
            <MeteogramPanel />
            <SkillPanel />
            <ModelStatusPanel />
          {:else if layout.view === 'places'}
            <PlacesSection onPicked={() => setView('forecast')} onManage={() => setView('settings')} />
          {:else if layout.view === 'settings'}
            <SettingsScreen {cartoKey} />
          {/if}
        </SectionPanel>
        {#if layout.view !== 'forecast'}
          <NextHourStrip timezone={bundle?.location.timezone ?? null} onOpen={() => setView('forecast')} />
        {/if}
      </div>
    {/if}
  </div>

  <BottomNav />
</div>

<style>
  .mobile {
    /* Round 2's phone sizes: every chip, bar and button a 40 px target, and
       the panel rows at 48 (the handoff's phone frames). */
    --chip-h: 40px;
    --seg-h: 40px;
    --btn-h: 40px;
    --ctl-fs: 14px;
    --btn-fs: 14px;
    --hint-fs: 12px;
    --panel-fs: 15px;
    --panel-row: 48px;
    --table-fs: 13px;
    --table-row: 30px;
    --toggle-h: 44px;
    --legend-fs: 15px;
    --switch-h: 40px;
    /* Under the search row and the layer chip: where a popup on the map may
       start, and the PWA toast. */
    --controls-bottom: calc(var(--safe-top) + 118px);
    position: relative;
    /* The Shell sizes the column (100dvh, less the connection banner). */
    height: 100%;
    display: flex;
    flex-direction: column;
    /* Not scrollable even by script: a focus() or a scrollIntoView() must
       never slide the whole layout, map and all, out of reach of a finger. */
    overflow: clip;
  }

  .stage {
    position: relative;
    flex: 1 1 auto;
    min-height: 0;
  }

  /* The map at its Map-section size the whole time (plan §5.3). */
  .map-area {
    position: absolute;
    inset: 0;
  }

  .section {
    position: absolute;
    inset: 0;
    z-index: 20;
    display: flex;
    flex-direction: column;
    background: var(--color-bg);
  }

  /* No z-index here: it would make the slot a stacking context and trap the
     search panel (fixed, z 41) under the layer chip beside it. The trigger
     takes the map chrome's level itself. */
  .search-slot {
    position: absolute;
    top: calc(var(--safe-top) + 12px);
    left: 12px;
    right: 68px;
  }

  .search-slot :global(.search-field) {
    position: relative;
    z-index: 18;
  }

  .chip-slot {
    position: absolute;
    z-index: 18;
    top: calc(var(--safe-top) + 68px);
    left: 12px;
    max-width: calc(100% - 92px);
  }

  .locate-slot {
    position: absolute;
    z-index: 18;
    top: calc(var(--safe-top) + 68px);
    right: 12px;
  }

  /* Under My location, in the same column; empty while north-up and flat. */
  .compass-slot {
    position: absolute;
    z-index: 18;
    top: calc(var(--safe-top) + 124px);
    right: 12px;
  }
</style>
